// src/pages/rakuten-event/RakutenUnderCartItemList.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const RakutenUnderCartItemList = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  // 📝 レガシーと同じカテゴリ定義を保持
  const categories = {
    'dress': 'パーティードレス',
    'bag': 'パーティーバッグ',
    'bolero': 'ボレロ・ショール'
  };

  // 各カテゴリごとの商品データを管理するState
  const [productsData, setProductsData] = useState({
    dress: [],
    bag: [],
    bolero: []
  });
  const [loading, setLoading] = useState(true);

  // ドラッグ＆ドロップ用の管理State
  const [dragInfo, setDragInfo] = useState({ category: null, index: null });

  // 🔃 データの初期取得
  const fetchAllCategoriesData = async () => {
    try {
      setLoading(true);

      // 本来は一括、またはカテゴリごとに FastAPI からデータを引く
      // 今回はレガシー同様、各カテゴリごとのエンドポイントから並び順(serial_num)でソートして取得することを想定
      const updatedData = {};
      for (const cate of Object.keys(categories)) {
        const res = await fetch(`${API_URL}/rakuten-cart-under/items?category=${cate}`);
        if (res.ok) {
          const resData = await res.json();
          // serial_num の昇順でソートを保証
          updatedData[cate] = (resData.data || []).sort((a, b) => a.serial_num - b.serial_num);
        } else {
          updatedData[cate] = [];
        }
      }
      setProductsData(updatedData);
    } catch (error) {
      console.error("カート下商品の取得に失敗しました:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCategoriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🗑️ 商品の削除処理
  const handleDelete = async (category, uuid, name) => {
    if (!window.confirm(`「${name}」をカート下リストから削除してよろしいですか？`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/rakuten-cart-under/items/${uuid}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchAllCategoriesData();
      } else {
        alert("削除に失敗しました。");
      }
    } catch (error) {
      console.error("削除エラー:", error);
    }
  };

  // 🔀 ドラッグ＆ドロップ並び替え：開始時
  const handleDragStart = (category, index) => {
    setDragInfo({ category, index });
  };

  // 🔀 ドラッグ＆ドロップ並び替え：侵入時（同一カテゴリ内のみ許可）
  const handleDragEnter = (category, index) => {
    if (dragInfo.category !== category || dragInfo.index === index) return;

    const currentList = [...productsData[category]];
    const draggedItem = currentList[dragInfo.index];

    // リストの入れ替え処理
    currentList.splice(dragInfo.index, 1);
    currentList.splice(index, 0, draggedItem);

    setDragInfo(prev => ({ ...prev, index }));
    setProductsData(prev => ({
      ...prev,
      [category]: currentList
    }));
  };

  // 🔀 ドラッグ＆ドロップ並び替え：終了・DBへソート順保存
  const handleDragEnd = async (category) => {
    const targetList = productsData[category];
    setDragInfo({ category: null, index: null });

    // FastAPIに送るソート用ペイロードを作成
    const sortData = targetList.map((item, index) => ({
      uuid: item.uuid,
      serial_num: index + 1
    }));

    try {
      const res = await fetch(`${API_URL}/rakuten-cart-under/items/sort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, sort_data: sortData })
      });

      if (!res.ok) {
        alert("並べ替えの保存に失敗しました。");
        fetchAllCategoriesData();
      }
    } catch (error) {
      console.error("並べ替え保存エラー:", error);
      fetchAllCategoriesData();
    }
  };

  return (
    <div>
      {/* パンくずリスト */}
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to="/">Home</Link></li>
        <li className="breadcrumb-item">カート下商品管理</li>
        <li className="breadcrumb-item active">下カート商品一覧</li>
      </ol>
      <h1 className="page-header">カート下登録アイテム一覧</h1>

      <div className="row">
        {Object.entries(categories).map(([cateKey, cateName]) => (
          <div className="col-xl-6 mb-4" key={cateKey}>
            <div className="panel panel-inverse h-100 d-flex flex-column mb-0">
              <div className="panel-heading">
                <h4 className="panel-title">{cateName}</h4>
                <div className="panel-heading-btn">
                  <button
                    className="btn btn-xs btn-success me-1"
                    onClick={() => navigate(`/rakuten-event/product/edit?key=${cateKey}`)}
                  >
                    <i className="fa fa-plus me-1"></i>新規追加
                  </button>
                </div>
              </div>

              <div className="panel-body flex-grow-1 p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <i className="fa fa-spinner fa-spin fa-2x text-muted"></i>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0" style={{ fontSize: '13px' }}>
                      <thead className="table-light">
                        <tr>
                          <th width="1%" className="text-center"><i className="fa fa-sort"></i></th>
                          <th width="10%">No.</th>
                          <th width="15%">画像</th>
                          <th width="50%">商品名</th>
                          <th width="24%">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productsData[cateKey]?.map((item, idx) => (
                          <tr
                            key={item.uuid}
                            style={{ cursor: 'grab' }}
                            draggable="true"
                            onDragStart={() => handleDragStart(cateKey, idx)}
                            onDragEnter={() => handleDragEnter(cateKey, idx)}
                            onDragEnd={() => handleDragEnd(cateKey)}
                            onDragOver={(e) => e.preventDefault()}
                          >
                            <td className="text-center text-muted align-middle"><i className="fa fa-bars"></i></td>
                            <td className="align-middle fw-bold text-center">{idx + 1}</td>
                            <td className="align-middle text-center">
                              <img
                                src={item.item_img || "https://www.rakuten.ne.jp/gold/twinklegirls/main/carousel/img/twinkle-girls.png"}
                                alt=""
                                className="rounded"
                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.src = "https://www.rakuten.ne.jp/gold/twinklegirls/main/carousel/img/twinkle-girls.png";
                                }}
                              />
                            </td>
                            <td className="align-middle fw-bold text-truncate" style={{ maxWidth: '200px' }}>
                              {item.item_name}
                            </td>
                            <td className="align-middle">
                              <button
                                className="btn btn-xs btn-primary me-1"
                                onClick={() => navigate(`/rakuten-event/product/edit?uuid=${item.uuid}`)}
                              >
                                編集
                              </button>
                              <button
                                className="btn btn-xs btn-danger"
                                onClick={() => handleDelete(cateKey, item.uuid, item.item_name)}
                              >
                                削除
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(!productsData[cateKey] || productsData[cateKey].length === 0) && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted p-4">
                              このカテゴリに登録されているアイテムはありません。
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RakutenUnderCartItemList;